import { Html5Qrcode } from "html5-qrcode";

import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Grid,
  Icon,
  Snackbar,
  Alert,
  Box,
  Typography,
  Button,
  IconButton,
} from "@mui/material";

function BarcodeReaderField({ displayName, fieldRef, options }) {
  const [fieldError, showFieldError] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  const [isScanning, setIsScanning] = useState(false);
  const [scannedText, setScannedText] = useState(null);
  const [scannerObj, setsCannerObj] = useState(null);

  const config = { fps: 10, qrbox: { width: 250, height: 250 } };

  function onScanSuccess(decodedText) {
    setScannedText(decodedText);
    closeScanning();
  }

  function openScanner(camera) {
    scannerObj.start(camera, config, onScanSuccess);
    setIsScanning(true);
  }

  function closeScanning() {
    if (scannerObj === null) return;

    try {
      scannerObj.stop();
      setIsScanning(false);
    } catch {}
  }

  function initScanning() {
    Html5Qrcode.getCameras()
      .then((devices) => {
        if (devices && devices.length) {
          var cameraId = devices[0].id;
          let camera = { deviceId: { exact: cameraId } };
          if (devices.length > 1) {
            camera = { facingMode: "environment" };
          }
          openScanner(camera);
        }
      })
      .catch((err) => {
        setErrorMsg(`Code scan error = ${err}`);
        showFieldError(true);
      });
  }

  useEffect(() => {
    setsCannerObj(new Html5Qrcode(`${fieldRef}_scanner`));
  }, []);

  return (
    <Box>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={5000}
        onClose={() => showFieldError(false)}
        open={fieldError}
      >
        <Alert severity="error" sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <Box sx={{ minWidth: 120, width: "100%" }}>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Typography variant="h6" fontWeight="light" fontSize="small" mb={2}>
              {displayName}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Box align="right">
              {!isScanning ? (
                <IconButton size="small" onClick={initScanning}>
                  <Icon>qr_code_scanner</Icon>
                </IconButton>
              ) : (
                <IconButton size="small" color="error" onClick={closeScanning}>
                  <Icon>close</Icon>
                </IconButton>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* CAPTURE BOX */}
        <Box
          sx={{
            border: "2px dashed #d2d6da",
            borderRadius: "12px",
            height: 220,
            position: "relative",
            overflow: "hidden",
            backgroundColor: "#f8f9fa",
            mt: 2,
          }}
        >
          {/* ALWAYS MOUNTED SCANNER (IMPORTANT) */}
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: isScanning ? "block" : "none",
            }}
          >
            <div
              id={`${fieldRef}_scanner`}
              style={{ width: "100%", height: "100%" }}
            />
          </Box>

          {/* EMPTY STATE */}
          {!isScanning && !scannedText && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box textAlign="center">
                <IconButton
                  color="primary"
                  onClick={initScanning}
                  sx={{
                    width: 64,
                    height: 64,
                    backgroundColor: "#e3f2fd",
                  }}
                >
                  <Icon fontSize="large">barcode_reader</Icon>
                </IconButton>

                <Typography variant="caption" mt={1} display="block">
                  Tap to scan barcode
                </Typography>
              </Box>
            </Box>
          )}

          {/* RESULT */}
          {scannedText && !isScanning && (
            <>
              <Typography
                variant="body2"
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: 2,
                  textAlign: "center",
                }}
              >
                {scannedText}
              </Typography>

              <IconButton
                onClick={() => setScannedText(null)}
                sx={{
                  position: "absolute",
                  top: 10,
                  right: 10,
                  backgroundColor: "white",
                  border: "1px solid rgba(0,0,0,0.2)",
                  boxShadow: 2,
                }}
              >
                <Icon color="error">close</Icon>
              </IconButton>
            </>
          )}
        </Box>
      </Box>

      <input
        id={`dff_${fieldRef}`}
        type="hidden"
        name={fieldRef}
        value={scannedText}
      />
    </Box>
  );
}

BarcodeReaderField.propTypes = {
  fieldRef: PropTypes.string.isRequired,
  displayName: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.any),
};

BarcodeReaderField.defaultProps = {
  options: null,
};

export default BarcodeReaderField;